import React, {useState, useContext, useEffect} from "react";
import {Box, Stack, Typography, IconButton, Tooltip, Button} from "@mui/material";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {HugeiconsIcon} from "@hugeicons/react";
import {Refresh01Icon, Add01Icon} from "@hugeicons/core-free-icons";
import {Context} from "../../../context/AppContext";
import {MCPServerGallery} from "./components/MCPServerGallery";
import {MCPServerDetailsPanelNew} from "./components/MCPServerDetailsPanelNew";
import {AddMCPServerModalNew} from "./components/AddMCPServerModalNew";
import {StatStrip} from "../dashboard/components/StatTile";
import {ServiceNotConfiguredBanner} from "../components/ServiceNotConfiguredBanner";
import {
    projectAtom,
    subProjectAtom,
    flowUidAtom,
    serversAtom,
    selectedServerAtom,
    mcpLoadingAtom,
    errorAtom
} from "./atoms";
import {toast} from "react-toastify";
import {useParams} from "react-router-dom";

export const MCPDiscoveryPageRegistry = ({project, subProject}) => {
    const {serverId} = useParams();

    const [servers, setServers] = useAtom(serversAtom);
    const [selectedServer, setSelectedServer] = useAtom(selectedServerAtom);
    const [loading, setLoading] = useAtom(mcpLoadingAtom);
    const [, setError] = useAtom(errorAtom);
    const setProjectAtom = useSetAtom(projectAtom);
    const setSubProjectAtom = useSetAtom(subProjectAtom);
    const selectedFlowUid = useAtomValue(flowUidAtom);

    const {state, getAllServersWithDetails} = useContext(Context);

    const [openAdd, setOpenAdd] = useState(false);

    // Sync route-driven project/language into atoms so child components can read them without prop drilling
    useEffect(() => {
        setProjectAtom(project);
    }, [project, setProjectAtom]);

    useEffect(() => {
        setSubProjectAtom(subProject);
    }, [subProject, setSubProjectAtom]);

    useEffect(() => {
        if (state?.mcpServers) {
            setServers(state.mcpServers);
            if (serverId) {
                const server = state.mcpServers.find(s => s.id === serverId);
                if (server) {
                    setSelectedServer(server);
                }
            }
        }
    }, [state?.mcpServers, setServers, serverId, setSelectedServer]);

    useEffect(() => {
        if (subProject?.flowUid && project?.backendConfiguration?.mcpRegistry) {
            fetchServers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subProject?.flowUid, project?.backendConfiguration?.mcpRegistry]);

    useEffect(() => {
        return () => setSelectedServer(null);
    }, [setSelectedServer]);

    const fetchServers = async () => {
        try {
            setLoading(true);
            setError(null);
            await getAllServersWithDetails({flowUid: subProject.flowUid, projectId: project.id, subProjectId: subProject.id});
            setSelectedServer(null);
        } catch (err) {
            setError(err.message);
            toast.error("Failed to load MCP servers");
            console.error("Error fetching servers:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleServerAdded = async () => {
        setOpenAdd(false);
        await fetchServers();
    };

    const scopedServers = selectedFlowUid ? servers.filter(s => s.flow_uid === selectedFlowUid) : servers;
    const totalServers = scopedServers.length;
    const activeServers = scopedServers.filter(s => s.active).length;
    const totalTools = scopedServers.reduce((sum, s) => sum + (s.tools?.length || 0), 0);

    return (
        <Stack gap={3} sx={{
            p: {xs: 2, md: 3}, maxWidth: {xs: "100%", md: "calc(100% - 128px)"}, width: "100%", mx: "auto",
            boxSizing: "border-box",
        }}>
            {/* Add MCP Server Modal */}
            <AddMCPServerModalNew
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                onSuccess={handleServerAdded}
            />

            {!project?.backendConfiguration?.mcpRegistry && (
                <ServiceNotConfiguredBanner label={"MCP Registry"}/>
            )}

            {selectedServer ? (
                <MCPServerDetailsPanelNew/>
            ) : (
                <>
                    <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
                        <Box>
                            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7", textTransform: "uppercase"}}>
                                MCP INTEGRATION
                            </Typography>
                            <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em", mt: 0.5}}>
                                Server Registry
                            </Typography>
                            <Typography sx={{fontSize: 14, color: "#6B6B80", mt: 0.5}}>
                                Discover, configure and monitor MCP servers and their capabilities
                            </Typography>
                        </Box>
                        <Stack direction="row" gap={1} alignItems="center">
                            <Button
                                size="small"
                                startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
                                onClick={() => setOpenAdd(true)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    borderRadius: '10px',
                                    color: '#374151',
                                    bgcolor: '#FAFAFD',
                                    '&:hover': {bgcolor: '#ECECF3'},
                                }}
                            >
                                Add Server
                            </Button>
                            <Tooltip title={"Refresh"}>
                                <IconButton onClick={fetchServers} disabled={loading} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
                                    <HugeiconsIcon icon={Refresh01Icon} size={17}/>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    <StatStrip items={[
                        {label: "Total Servers", value: totalServers, note: `${activeServers} active · ${totalServers - activeServers} inactive`},
                        {
                            label: "Active", value: activeServers,
                            note: totalServers ? `${Math.round((activeServers / totalServers) * 100)}% of total servers` : "No servers yet",
                            meter: totalServers ? activeServers / totalServers : 0,
                        },
                        {label: "Tools Available", value: totalTools, note: "Callable from each server"},
                    ]}/>

                    <MCPServerGallery onAddClick={() => setOpenAdd(true)}/>
                </>
            )}
        </Stack>
    );
};
